/**
 * Domain-neutral formatting helpers.
 *
 * Lives here rather than in a page-specific util module so that shared components under
 * `components/ui/` can use them: a `components/ui/` file must not import an AR-Dunning util.
 * `utils/arDunningFormat.ts` re-exports both, so existing AR imports keep working.
 */

/** ISO / yyyy-mm-dd → dd.mm.yyyy (no toISOString — that would shift the date in CET/CEST). */
export function formatDate(value: string | null | undefined): string {
    if (!value) return '–';
    const [y, m, d] = value.slice(0, 10).split('-');
    if (!y || !m || !d) return value;
    return `${d}.${m}.${y}`;
}

/** ISO timestamp → dd.mm.yyyy, hh:mm (local). Falls back to formatDate for date-only input. */
export function formatDateTime(value: string | null | undefined): string {
    if (!value) return '–';
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return formatDate(value);
    return dt.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Mark a zone-less ISO timestamp as UTC.
 *
 * SQL's `CONVERT(..., 126)` emits `2026-08-20T05:49:27` with no zone designator, and
 * JS parses that form as LOCAL time. A column stored with SYSUTCDATETIME() therefore
 * renders two hours early all summer — silently, because the string looks perfectly valid.
 *
 * Idempotent: a value that already carries `Z` or a `+01:00`-style offset is returned
 * untouched, so it is safe whether or not the API has been redeployed with the explicit
 * marker. Date-only values are left alone — a calendar day has no instant.
 */
export function toUtcInstant(value: string | null | undefined): string | null {
    if (!value) return null;
    if (!value.includes('T')) return value;
    return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}

/**
 * UTC timestamp → dd.mm.yyyy, hh:mm in Swiss time.
 *
 * Pinned to Europe/Zurich rather than the browser's zone on purpose: these are Swiss
 * business timestamps, and a screenshot has to mean the same thing to the CFO in Basel,
 * the IT team in Italy and anyone looking from Portugal or Turkey.
 *
 * Separate from formatDateTime because that one is shared with pages whose inputs are not
 * demonstrably UTC — changing it under them would move THEIR clocks for a fix they did not
 * ask for.
 */
export function formatDateTimeCH(value: string | null | undefined): string {
    const normalized = toUtcInstant(value);
    if (!normalized) return '–';
    const dt = new Date(normalized);
    if (isNaN(dt.getTime())) return formatDate(value);
    return dt.toLocaleString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'Europe/Zurich',
    });
}
